import { CSSProperties, forwardRef, useCallback, useMemo } from 'react';
import {
  CircularProgress,
  IconButton,
  InputAdornment,
  withStyles,
  OutlinedInputProps
} from '@material-ui/core';
import { debounce } from 'lodash';
import { Close } from '@material-ui/icons';
import clsx from 'clsx';

import { SearchIcon as Search } from '../../sicons/regular-icons';
import { SearchField, useStyles } from './style';
import { TextColor } from '../../../enums';

enum IconPosition {
  start = 'start',
  end = 'end'
}

const iconColor = TextColor.Grey;

const Loader = withStyles(({ spacing }) => ({
  root: {
    marginRight: spacing(1),
    color: iconColor
  }
}))(CircularProgress);

const EndIcon = ({
  loading,
  iconPosition,
  search,
  handleInputClear
}: Pick<SSearchInputPropsV2, 'loading' | 'iconPosition' | 'search'> & {
  handleInputClear: () => void;
}) => {
  if (loading) return <Loader size={18} />;

  if (iconPosition === IconPosition.end && !search)
    return <Search style={{ fontSize: '1.15rem' }} />;

  if (search)
    return (
      <IconButton size="small" onClick={handleInputClear}>
        <Close fontSize="small" style={{ color: iconColor }} />
      </IconButton>
    );

  return null;
};

export interface SSearchInputPropsV2
  extends Omit<OutlinedInputProps, 'onChange'> {
  placeholder?: string;
  loading?: boolean;
  width?: number | string;
  height?: number | string;
  className?: string;
  style?: CSSProperties;
  search: string;
  /**
   * onChange is debounced and called when the user stops typing
   * Passed functions must be memoized by the caller (useCallback)
   */
  onChange(val: string): void;
  setSearch(val: string): void;
  iconPosition?: keyof typeof IconPosition;
}

export const SSearchInputV2 = forwardRef<HTMLInputElement, SSearchInputPropsV2>(
  (
    {
      placeholder = 'Search...',
      width = 320,
      height,
      loading = false,
      className,
      style,
      iconPosition = IconPosition.end,
      search,
      onChange,
      setSearch,
      ...rest
    },
    ref
  ) => {
    const classes = useStyles({ width, height });

    const debouncedOnChange = useMemo(
      () => debounce((val: string) => onChange(val), 500),
      [onChange]
    );

    const handleChange = useCallback(
      (val: string) => debouncedOnChange(val),
      [debouncedOnChange]
    );

    const handleInputClear = useCallback(() => {
      setSearch('');
      debouncedOnChange.cancel();
    }, [debouncedOnChange, setSearch]);

    return (
      <SearchField
        inputRef={ref}
        placeholder={placeholder}
        className={clsx(classes.wrapper, className)}
        style={style}
        onChange={e => {
          handleChange(e.target.value);
          setSearch(e.target.value);
        }}
        value={search}
        fullWidth
        startAdornment={
          iconPosition === IconPosition.start && (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          )
        }
        endAdornment={
          <InputAdornment position="end">
            <EndIcon
              loading={loading}
              iconPosition={iconPosition}
              search={search}
              handleInputClear={handleInputClear}
            />
          </InputAdornment>
        }
        {...rest}
      />
    );
  }
);
